import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Point } from 'geojson';
import { firstValueFrom, from, ReplaySubject, Subject } from 'rxjs';
import { find, map, pluck, switchMap, takeUntil } from 'rxjs/operators';
import { ApiGatewayService } from '~app/shared/api-gateway.service';
import { MapElement } from '~app/shared/map/map-element.model';
import { MapService } from '~app/shared/map/map.service';
import { TAnalyzePointsResult, Tv2l } from './v2l.type';
import distance from '@turf/distance';

//// Properties needs further TLC..
const staticProps = {
  prop1: 1,
  prop2: 2,
};

@Injectable()
export class V2LService extends ApiGatewayService implements OnDestroy {
  private readonly v2lLayerVisible$ = new Subject<boolean>();
  private readonly ngUnsubscribe$ = new Subject<void>();
  private readonly analysis$ = new ReplaySubject<TAnalyzePointsResult>(1);
  private analyzesInitialized = false;

  constructor(
    protected http: HttpClient,
  ) {
    super(http, 'v2l-api');
    console.log('V2LService default constructor');
  }

  ngOnDestroy(): void {
    this.v2lLayerVisible$.complete();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  /**
   * Query and draw V2L distinct coordinate points to canvas
   * @param ms MapService reference
   */
  public initMapItems(ms: MapService) {
    console.log('V2LService initMapItems');

    //// construct listener of v2l layer's toggle's
    //// single v2l layer per tenant
    ms.layersR$.pipe(
      find(layer => layer.metadata.v2lLayer),
      switchMap(layer => layer.isVisible$),
      takeUntil(this.ngUnsubscribe$)
    ).subscribe((isVisible: boolean) => {
      this.v2lLayerVisible$.next(isVisible);
    });

    //// if layer is activated, fetch data if not yet fetched
    this.v2lLayerVisible$.pipe(
      takeUntil(this.ngUnsubscribe$)
    ).subscribe( (v2lLayerVisibility) => {
      console.log(`v2l visibility: ${v2lLayerVisibility}`);
      if (!this.analyzesInitialized && v2lLayerVisibility) {
        from(this.getDistinctCoordinates()).subscribe({
          next: (points: TAnalyzePointsResult) => {
            this.analysis$.next(points);
          }
        });
        this.analyzesInitialized = true;
      }
    });

    //// draw the items
    this.analysis$.pipe(
      takeUntil(this.ngUnsubscribe$)
    ).subscribe( (points) => {
      //// at moment, we have single v2l layer for tenant, if configuration error (multiple v2l layers defined) picking the first one
      const v2lLayer = ms.getLayerList().find((layer) => layer.metadata.v2lLayer);
      const elements = points.data.map(point => {
        return new MapElement<Point>(Math.random(), v2lLayer, staticProps, 'Feature', point);
      });
      if (v2lLayer) {
        console.log(`drawing items on ${v2lLayer.id}`);
        v2lLayer.pushFeatures(elements);
      }
    });
  }

  /**
   * fetch distinct coordinate pairs (Point) from V2L
   * NOTE: let this be, as long as the other endpoints get stabilized.
   * @returns array of distinct Points
   */
  public getDistinctCoordinates(): Promise<TAnalyzePointsResult> {
    return this.get(['v2l-egress', 'distinct', 'NaytteenottoMatka.Naytteenotto.NaytteenottoPaikka.geometry']);
  }

  /**
   * analysis for specific coordinates
   * @param point
   * @returns
   */
  public getLatestAnalysisForCoordinates(point: Point): Promise<{data: [Tv2l]}> {
    return this.get(['v2l-egress', 'filter'], {
      x: `${point.coordinates[0]}`,
      y: `${point.coordinates[1]}`,
      maxDistance: '5', //// should be exact match!
      limit: '1',
      sort: `{'updatedAt': -1}`
    });
  }

  /**
   * find out coordinate match between clicked point and v2l laboratory analysis coordinates
   * return 'original' v2l geojson point if decent match is found
   * @param feature
   * @returns
   */
  public getHasAnalysis(feature: MapElement<Point, any>): Promise<Point | undefined> {
    return firstValueFrom(this.analysis$.pipe(
      takeUntil(this.ngUnsubscribe$),
      pluck('data'),
      map(pps => pps.find(point => {
        //// rego feature have some offset
        //// accuracy of the clicked feature coordinates depends on map zoom level!
        if (point.coordinates === feature.geometry.coordinates) {
          console.log('match found with direct coordinate comparison');
          return true;
        }
        if (distance(point, feature.geometry, {units: 'meters'}) < 10) {
          console.log('match found with distance search');
          return true;
        }
      }))
    ));
  }
}
